/**
 * Cloudflare Turnstile bot check (docs/security/turnstile.md).
 *
 * Renders nothing without a site key. With one it loads Cloudflare's script
 * once, on demand (`render=explicit`, only when a widget first mounts), and
 * renders a widget with the given `action`, the `auto` theme and a flexible
 * width. Managed widgets only ask the visitor to click when Cloudflare is
 * unsure (`appearance: 'interaction-only'`); an invisible widget never shows.
 *
 * `onToken` receives each fresh token and `null` when it expires or fails.
 * A token works once: after a submit (pass or fail) call `reset()` from the
 * ref for a new one. On a widget error it resets itself, up to 3 times.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, type ForwardedRef } from 'react';
import { cx } from '../lib/cx.js';
import './turnstile.css';

export const TURNSTILE_SCRIPT =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'flexible' | 'compact';
  appearance?: 'always' | 'execute' | 'interaction-only';
  execution?: 'render' | 'execute';
  'refresh-expired'?: 'auto' | 'manual' | 'never';
  'response-field'?: boolean;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  'error-callback'?: (code?: string) => boolean;
}

export interface TurnstileGlobal {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string | undefined;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
  execute?: (id?: string) => void;
  getResponse?: (id?: string) => string | undefined;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

let loading: Promise<TurnstileGlobal> | null = null;

/** Loads Cloudflare's script once per page. */
export function loadTurnstile(): Promise<TurnstileGlobal> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileGlobal>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = TURNSTILE_SCRIPT;
    s.async = true;
    s.defer = true;
    s.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing'));
    s.onerror = () => {
      loading = null; // a later mount may try again
      s.remove();
      reject(new Error('turnstile failed to load'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export interface TurnstileHandle {
  /** Discard the current token and solve again (after every submit). */
  reset: () => void;
}

export interface TurnstileProps {
  /** Public site key; `null` renders nothing. */
  siteKey: string | null;
  /** Action name the API checks, `[A-Za-z0-9_-]{1,32}`. */
  action: string;
  onToken: (token: string | null) => void;
  /** Called when the script cannot load or the widget keeps failing. */
  onUnavailable?: () => void;
  /** `interaction-only` (default) shows the widget only when a click is needed. */
  appearance?: 'always' | 'interaction-only' | 'execute';
  className?: string;
}

const MAX_ERROR_RESETS = 3;

function TurnstileInner(
  {
    siteKey,
    action,
    onToken,
    onUnavailable,
    appearance = 'interaction-only',
    className,
  }: TurnstileProps,
  ref: ForwardedRef<TurnstileHandle>,
) {
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const cb = useRef({ onToken, onUnavailable });
  cb.current = { onToken, onUnavailable };

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        cb.current.onToken(null);
        if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
      },
    }),
    [],
  );

  useEffect(() => {
    if (!siteKey || !box.current) return;
    let cancelled = false;
    let errors = 0;
    let retry: ReturnType<typeof setTimeout> | null = null;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !box.current) return;
        widget.current =
          ts.render(box.current, {
            sitekey: siteKey,
            action,
            theme: 'auto',
            size: 'flexible',
            appearance,
            'refresh-expired': 'auto',
            'response-field': false,
            callback: (t) => {
              errors = 0;
              cb.current.onToken(t);
            },
            'expired-callback': () => cb.current.onToken(null),
            'timeout-callback': () => cb.current.onToken(null),
            'error-callback': () => {
              cb.current.onToken(null);
              errors += 1;
              if (errors > MAX_ERROR_RESETS) {
                cb.current.onUnavailable?.();
                return true;
              }
              retry = setTimeout(() => {
                if (!cancelled && widget.current) ts.reset(widget.current);
              }, 1000 * errors);
              return true; // handled: no console noise from Cloudflare
            },
          }) ?? null;
      })
      .catch(() => {
        if (cancelled) return;
        cb.current.onToken(null);
        cb.current.onUnavailable?.();
      });
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey, action, appearance]);

  if (!siteKey) return null;
  return <div ref={box} className={cx('vel-turnstile', className)} data-action={action} />;
}

export const Turnstile = forwardRef(TurnstileInner);
Turnstile.displayName = 'Turnstile';
